"use client";

import { usePathname } from "next/navigation";
import { useAppSelector } from "@/store/hooks";
import { FileText, Phone } from "lucide-react";
import { buildTelHref, formatPhoneForDisplay } from "@/lib/phone";

/**
 * Client component that displays call info (incident number, phone, PIN) in the header.
 * Only visible on active call and incident report pages.
 */
export function HeaderCallInfo() {
  const pathname = usePathname();
  const incidentNumber = useAppSelector((state) => state.callInfo.incidentNumber);
  const phonePin = useAppSelector((state) => state.callInfo.phonePin);
  const reduxPhoneNumber = useAppSelector((state) => state.callInfo.phoneNumber);

  // The number comes from the call row via Redux. There is no client-side
  // env fallback: NEXT_PUBLIC_PHONE_NUMBER was never inlined into this bundle
  // (not in next.config env, not a Dockerfile ARG), so the fallback that used
  // to sit here was always undefined. See lib/phone.ts.
  const phoneNumber = reduxPhoneNumber;

  // Only show on call pages and incident report
  const isCallPage = pathname?.startsWith("/calls/") && pathname !== "/calls";
  const isIncidentReport = pathname?.includes("/incident-report");
  const shouldShow = isCallPage || isIncidentReport;

  if (!shouldShow) {
    return null;
  }

  if (!incidentNumber && !phoneNumber) {
    return null;
  }

  return (
    <>
      {/* Separator before call info */}
      <span className="text-slate-600 flex-shrink-0 pointer-events-none" aria-hidden="true">|</span>

      <div className="flex items-center gap-3 text-sm flex-shrink-0">
        {/* Incident Number - only show if set */}
        {incidentNumber && (
          <div className="flex items-center gap-1.5 text-slate-300">
            <FileText className="w-4 h-4 text-blue-400" />
            <span className="text-white font-bold">{incidentNumber}</span>
          </div>
        )}

        {/* Phone Number - always show when on call page. Wrapped in a tel: link
            so a mobile citizen can tap to dial in (the PIN is sent as a
            post-dial DTMF sequence after a short pause). On desktop the link
            still works via the OS's tel: handler (FaceTime, Phone Link, etc.). */}
        {phoneNumber && (
          <>
            {incidentNumber && <span className="text-slate-600 pointer-events-none" aria-hidden="true">|</span>}
            <a
              href={buildTelHref(phoneNumber, phonePin)}
              className="flex items-center gap-1.5 text-slate-300 hover:text-white focus:outline-none focus:ring-2 focus:ring-green-400/50 rounded-md px-1 -mx-1 transition-colors"
              title={phonePin ? `Tap to call (PIN ${phonePin} will be auto-dialed)` : 'Tap to call'}
            >
              <Phone className="w-4 h-4 text-green-400" />
              <span className="text-white font-medium underline decoration-dotted underline-offset-2 decoration-slate-500">
                {formatPhoneForDisplay(phoneNumber)}
              </span>
              {/* PIN - only show when available */}
              {phonePin && (
                <>
                  <span className="text-slate-400">PIN:</span>
                  <span className="text-white font-bold">{phonePin}</span>
                </>
              )}
            </a>
          </>
        )}
      </div>
    </>
  );
}
